import React, { useCallback, useEffect, useRef, useState } from 'react';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import { callTokenApi } from '../../../../../../apiCaller';
import { API_AI_INSIGHTS_INTENT_PROPERTY, TYPE_SHOW_UNSAVE_CHANGE } from '../../../../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { setRuleHaveEditting } from '../../../../../../actions/common';

const SettingNameDescription = React.lazy(() => import('./steps/SettingNameDescription'));
const ObjectFieldType = React.lazy(() => import('./steps/ObjectFieldType'));

export const ManagePropertiesContext = React.createContext({});

const ManageIntentRules = ({ attributes }) => {
    const { ruleId: id, setRefetch, setListRules, setOptionProperties, setNewProperties, object, storeValue, analysisType } = attributes;
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [stepsData, setStepsData] = useState({});
    const [initStepsData, setInitStepsData] = useState({});
    const [dirtySteps, setDirtySteps] = useState({});
    const [stepSubmitSnapshot, setStepSubmitSnapshot] = useState({});
    const activeAccount = useSelector((state) => state.subscriber.activeAccount);

    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const fetchIntentProperty = useCallback(async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const response = await callTokenApi(
                `${API_AI_INSIGHTS_INTENT_PROPERTY}/${id}`,
                'GET'
            );
            if (response?.status === 200) {
                const { data } = response.data;
                if (data && isMounted.current) {
                    setStepsData(data);
                    setInitStepsData(data);
                    setActiveStep(1);
                }
            }
        } finally {
            if (isMounted.current) setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!activeAccount?.accountId) return;

        fetchIntentProperty();
    }, [activeAccount?.accountId, fetchIntentProperty]);

    const setStepDirty = useCallback((step, dirty) => {
        setDirtySteps((prev) => {
            const next = { ...prev, [step]: dirty };
            const show = Object.values(next).some(Boolean);
            dispatch(
                setRuleHaveEditting({
                    show,
                    type: show ? TYPE_SHOW_UNSAVE_CHANGE.CREATE_SIMPLE : "",
                })
            );
            return next;
        });
    }, [dispatch]);

    return (
        <React.Suspense fallback={<CenterSpinner />}>
            <div className="rule-detail account-connected-detail">
                {isLoading ? (
                    <CenterSpinner />
                ) : (
                    <div className="cvr-create-new">
                        <h1>{id ? `Edit Intent Property: ${stepsData.name || ""}` : `Create Intent Property`}</h1>
                        <p>Create and manage the fields on your objects used to indicate intent.</p>

                        <ManagePropertiesContext.Provider
                            value={{
                                id,
                                activeStep,
                                setActiveStep,
                                initStepsData,
                                setInitStepsData,
                                stepsData,
                                setStepsData,
                                setListRules,
                                setOptionProperties,
                                setRefetch,
                                onFormDirty: setStepDirty,
                                dirtySteps,
                                setDirtySteps,
                                stepSubmitSnapshot,
                                setStepSubmitSnapshot,
                                object,
                                setNewProperties,
                                analysisType,
                                storeValue,
                            }}
                        >
                            <>
                                <ObjectFieldType />
                                <SettingNameDescription />
                            </>
                        </ManagePropertiesContext.Provider>
                    </div>
                )}
            </div>
        </React.Suspense>
    );
};

export default ManageIntentRules;
